import { AddressCompletionField, AddressRecoveryReason, type AddressRecoveryRecord } from '@navio/nsdk';
import { revalidateLogic } from '@tanstack/react-form';
import { NoddiIcon } from 'noddi-ui-common';
import { ErrorMessage, NoddiButton, NoddiDialog, useAppForm } from 'noddi-ui-web';
import { useId } from 'react';
import { z } from 'zod';
import type { AddressRecoveryTranslations } from './types';
import type { AddressCompletion } from './useAddressResolution';

const defaultTranslations: AddressRecoveryTranslations = {
  heading: 'Complete your address',
  description: 'Add the missing address details to continue.',
  streetNumber: 'House number',
  zipCode: 'Postal code',
  city: 'City',
  continue: 'Continue',
  retry: 'Try again',
  retryNotice: 'We could not look up this address. Please try again.',
  selectAnother: 'Please select a more specific address with a house number.',
  invalidPostalCode: 'Enter a valid postal code.',
  invalidStreetNumber: 'Enter a valid house number.',
  checkDetails: 'We could not verify this address. Check the details and try again.',
  required: {
    streetNumber: 'Enter a house number.',
    zipCode: 'Enter a postal code.',
    city: 'Enter a city.'
  }
};

const fields = {
  [AddressCompletionField.STREET_NUMBER]: 'streetNumber',
  [AddressCompletionField.ZIP_CODE]: 'zipCode',
  [AddressCompletionField.CITY]: 'city'
} as const;

type Props = {
  recovery: AddressRecoveryRecord;
  failed: boolean;
  loading: boolean;
  onSubmit: (completion: AddressCompletion) => void;
  onRetry: () => void;
  onCancel: () => void;
  translations?: AddressRecoveryTranslations;
};

export const AddressRecoveryForm = ({
  recovery,
  failed,
  loading,
  onSubmit,
  onRetry,
  onCancel,
  translations
}: Props) => {
  const texts = translations ?? defaultTranslations;
  const formId = useId();
  const schema = z
    .object({ streetNumber: z.string(), zipCode: z.string(), city: z.string() })
    .superRefine((value, ctx) => {
      if (
        recovery.streetNumberPattern &&
        recovery.missingFields.includes(AddressCompletionField.STREET_NUMBER) &&
        value.streetNumber.trim() &&
        !new RegExp(recovery.streetNumberPattern).test(value.streetNumber.trim())
      ) {
        ctx.addIssue({ code: 'custom', path: ['streetNumber'], message: texts.invalidStreetNumber });
      }
      if (
        recovery.zipCodePattern &&
        recovery.missingFields.includes(AddressCompletionField.ZIP_CODE) &&
        value.zipCode.trim() &&
        !new RegExp(recovery.zipCodePattern).test(value.zipCode.trim())
      ) {
        ctx.addIssue({ code: 'custom', path: ['zipCode'], message: texts.invalidPostalCode });
      }
      for (const missing of recovery.missingFields) {
        const name = fields[missing];
        if (!value[name].trim()) {
          ctx.addIssue({ code: 'custom', path: [name], message: texts.required[name] });
        }
      }
    });
  const form = useAppForm({
    defaultValues: { streetNumber: '', zipCode: '', city: '' },
    validationLogic: revalidateLogic(),
    validators: { onDynamic: schema },
    onSubmit: ({ value }) =>
      onSubmit(Object.fromEntries(recovery.missingFields.map((field) => [fields[field], value[fields[field]].trim()])))
  });

  if (!recovery.streetName || !recovery.countryCode || recovery.missingFields.length === 0) {
    return (
      <div className='flex flex-col gap-2' role='status'>
        <ErrorMessage
          message={recovery.reason === AddressRecoveryReason.UNSUPPORTED ? texts.selectAnother : texts.retryNotice}
        />
        {recovery.reason === AddressRecoveryReason.UNAVAILABLE && (
          <NoddiButton type='button' variant='outline' loading={loading} onClick={onRetry}>
            {texts.retry}
          </NoddiButton>
        )}
      </div>
    );
  }

  return (
    <form.AppForm>
      <NoddiDialog
        open
        onClose={onCancel}
        title={texts.heading}
        description={texts.description}
        footer={
          <form.Subscribe selector={(state) => state.values}>
            {(values) => {
              const missing = recovery.missingFields.find((field) => !values[fields[field]].trim());
              return (
                <form.SubscribeButton
                  formId={formId}
                  label={texts.continue}
                  disabled={loading}
                  disabledReason={missing ? texts.required[fields[missing]] : undefined}
                />
              );
            }}
          </form.Subscribe>
        }
      >
        <form
          id={formId}
          className='flex flex-col gap-3'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          {failed && (
            <div role='alert'>
              <ErrorMessage
                message={
                  recovery.reason === AddressRecoveryReason.UNAVAILABLE
                    ? texts.retryNotice
                    : recovery.reason === AddressRecoveryReason.UNSUPPORTED
                      ? texts.selectAnother
                      : texts.checkDetails
                }
              />
            </div>
          )}
          <div className='flex items-center gap-2 text-sm'>
            <NoddiIcon name='MapPin' className='shrink-0 text-muted-foreground' />
            <span>
              {[recovery.streetName, recovery.streetNumber, recovery.zipCode, recovery.city].filter(Boolean).join(' ')}
            </span>
          </div>
          {recovery.missingFields.map((missing) => {
            const name = fields[missing];
            return (
              <form.AppField key={name} name={name}>
                {(field) => (
                  <field.Input
                    label={texts[name]}
                    maxLength={name === 'streetNumber' ? 10 : name === 'zipCode' ? 12 : 1024}
                  />
                )}
              </form.AppField>
            );
          })}
        </form>
      </NoddiDialog>
    </form.AppForm>
  );
};
